import React, { useState } from 'react';
import { Cpu, Container, Activity, Download, Play, Square, Trash2, HardDrive, Search, MemoryStick, GitBranch, Timer } from 'lucide-react';

const initialModels = [
  {
    id: 1, name: 'YOLOv8', description: 'Real-time object detection',
    status: 'running', downloaded: true, running: true, size: 6144, container: 'yolov8',
    startTime: new Date(Date.now() - 7200000).toISOString(), port: 8081, totalRequests: 847
  },
  {
    id: 2, name: 'LLaMA 3', description: 'Large language model for chat',
    status: 'downloaded', downloaded: true, running: false, size: 40960, container: 'llama3',
    startTime: null, port: null, totalRequests: 0
  },
  {
    id: 3, name: 'Whisper', description: 'OpenAI speech recognition model',
    status: 'not_downloaded', downloaded: false, running: false, size: 3072, container: 'whisper',
    startTime: null, port: null, totalRequests: 0
  },
  {
    id: 4, name: 'SAM', description: 'Segment Anything Model by Meta',
    status: 'not_downloaded', downloaded: false, running: false, size: 8192, container: 'sam',
    startTime: null, port: null, totalRequests: 0
  },
];

const mockSystemStats = {
  cpuUsage: 34.2,
  ramUsage: 61.5,
  diskUsage: 51200,
  totalRequests: 1247,
  upTime: '14h 23m'
};

const STATUS_COLORS = {
  running: 'text-green-600 bg-green-100',
  downloaded: 'text-cyan-600 bg-cyan-100',
  not_downloaded: 'text-gray-500 bg-gray-100',
  downloading: 'text-blue-600 bg-blue-100',
  deleting: 'text-red-500 bg-red-100',
  isBeingRun: 'text-yellow-600 bg-yellow-100',
  isBeingStop: 'text-orange-600 bg-orange-100',
};

const STATUS_LABELS = {
  running: 'Running',
  downloaded: 'Downloaded',
  not_downloaded: 'Available',
  downloading: 'Downloading',
  deleting: 'Deleting',
  isBeingRun: 'Starting',
  isBeingStop: 'Stopping',
};

const timeAgo = (isoString) => {
  if (!isoString) return '-';
  const diffMs = Date.now() - new Date(isoString);
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

const AnimatedBar = ({ color }) => (
  <div className="relative w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
    <div className={`absolute left-0 top-0 h-full w-1/2 ${color} animate-pulse rounded-full`} />
  </div>
);

const ModelsTab = ({ models, onDownload, onStart, onStop, onDelete }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h2 className="text-lg font-bold text-gray-800">Model Management</h2>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
        <input
          type="text"
          placeholder="Search model..."
          readOnly
          className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none cursor-default w-36"
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {models.map((model) => {
        const isBusy = ['downloading', 'deleting', 'isBeingRun', 'isBeingStop'].includes(model.status);
        return (
          <div key={model.id} className="bg-white rounded-xl shadow border border-gray-200 p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">{model.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5 leading-tight">{model.description}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[model.status] || STATUS_COLORS.not_downloaded}`}>
                {STATUS_LABELS[model.status] || model.status}
              </span>
            </div>

            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Size:</span>
                <span className="font-medium">{(model.size / 1024).toFixed(1)} GB</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Container:</span>
                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{model.container}</span>
              </div>
            </div>

            {model.status === 'downloading' && (
              <div className="mb-3">
                <p className="text-xs text-blue-600 mb-1">Downloading...</p>
                <AnimatedBar color="bg-gradient-to-r from-cyan-400 to-blue-500" />
              </div>
            )}
            {model.status === 'deleting' && (
              <div className="mb-3">
                <p className="text-xs text-red-500 mb-1">Deleting...</p>
                <AnimatedBar color="bg-gradient-to-r from-red-400 to-red-600" />
              </div>
            )}
            {model.status === 'isBeingRun' && (
              <div className="mb-3">
                <p className="text-xs text-yellow-600 mb-1">Starting...</p>
                <AnimatedBar color="bg-gradient-to-r from-yellow-300 to-green-500" />
              </div>
            )}
            {model.status === 'isBeingStop' && (
              <div className="mb-3">
                <p className="text-xs text-orange-500 mb-1">Stopping...</p>
                <AnimatedBar color="bg-gradient-to-r from-orange-300 to-red-500" />
              </div>
            )}

            <div className="flex gap-2">
              {!model.downloaded && model.status !== 'downloading' && (
                <button
                  onClick={() => onDownload(model.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-xs font-medium hover:from-cyan-600 hover:to-blue-600 transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Download
                </button>
              )}

              {model.downloaded && !isBusy && !model.running && (
                <>
                  <button
                    onClick={() => onStart(model.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                  >
                    <Play className="w-3 h-3" />
                    Run
                  </button>
                  <button
                    onClick={() => onDelete(model.id)}
                    className="px-2.5 py-1.5 border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}

              {model.running && !isBusy && (
                <button
                  onClick={() => onStop(model.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                >
                  <Square className="w-3 h-3" />
                  Stop
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const ContainersTab = ({ models }) => {
  const containers = models
    .filter(m => m.running || m.status === 'running')
    .map(m => ({
      id: m.id,
      name: `${m.container}-prod`,
      image: `ainextus/${m.container}:latest`,
      isRunning: m.running,
      startTime: m.startTime,
      api: `http://localhost:8300/ainextus/${m.container}`,
      port: m.port || 8080,
      totalRequests: m.totalRequests || 0,
    }));

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-gray-800">Active Containers</h2>
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gradient-to-r from-purple-200 to-blue-200">
              <tr>
                {['Name', 'Image', 'Status', 'Uptime', 'API Address', 'Port', 'Requests'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {containers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400">No running containers</td>
                </tr>
              ) : (
                containers.map(c => (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono">{c.image}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium text-green-600 bg-green-100">Running</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{timeAgo(c.startTime)}</td>
                    <td className="px-4 py-3 text-gray-700 font-mono">{c.api}</td>
                    <td className="px-4 py-3 text-gray-600">{c.port}</td>
                    <td className="px-4 py-3 text-gray-600">{c.totalRequests}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SystemTab = ({ runningCount }) => (
  <div className="space-y-4">
    <h2 className="text-lg font-bold text-gray-800">System Status</h2>
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Disk Usage', value: `${(mockSystemStats.diskUsage / 1024).toFixed(1)} GB`, icon: <HardDrive className="w-6 h-6 text-cyan-500" />, bar: 47 },
        { label: 'CPU Usage', value: `${mockSystemStats.cpuUsage.toFixed(1)}%`, icon: <Activity className="w-6 h-6 text-orange-500" />, bar: mockSystemStats.cpuUsage },
        { label: 'RAM Usage', value: `${mockSystemStats.ramUsage.toFixed(1)}%`, icon: <MemoryStick className="w-6 h-6 text-green-500" />, bar: mockSystemStats.ramUsage },
        { label: 'Active Containers', value: runningCount, icon: <Container className="w-6 h-6 text-blue-500" />, bar: null },
        { label: 'Total Requests', value: mockSystemStats.totalRequests.toLocaleString(), icon: <GitBranch className="w-6 h-6 text-red-500" />, bar: null },
        { label: 'System Uptime', value: mockSystemStats.upTime, icon: <Timer className="w-6 h-6 text-yellow-500" />, bar: null },
      ].map((item, i) => (
        <div key={i} className="bg-white p-4 rounded-xl shadow border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
              <p className="text-xl font-bold text-gray-800">{item.value}</p>
            </div>
            {item.icon}
          </div>
          {item.bar !== null && (
            <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
              <div
                className="h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-blue-400"
                style={{ width: `${item.bar}%` }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const DashboardPreview = () => {
  const [activeTab, setActiveTab] = useState('models');
  const [models, setModels] = useState(initialModels);

  const delay = (ms) => new Promise(r => setTimeout(r, ms));

  const updateModel = (id, patch) =>
    setModels(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));

  const handleDownload = async (id) => {
    updateModel(id, { status: 'downloading' });
    await delay(2000);
    updateModel(id, { status: 'downloaded', downloaded: true });
  };

  const handleStart = async (id) => {
    updateModel(id, { status: 'isBeingRun' });
    await delay(1500);
    updateModel(id, { status: 'running', running: true, startTime: new Date().toISOString(), port: 8080 + id, totalRequests: 0 });
  };

  const handleStop = async (id) => {
    updateModel(id, { status: 'isBeingStop' });
    await delay(1500);
    updateModel(id, { status: 'downloaded', running: false, startTime: null });
  };

  const handleDelete = async (id) => {
    updateModel(id, { status: 'deleting' });
    await delay(1500);
    updateModel(id, { status: 'not_downloaded', downloaded: false });
  };

  const runningCount = models.filter(m => m.running).length;

  const menuItems = [
    { id: 'models', icon: Cpu, label: 'Model Management' },
    { id: 'containers', icon: Container, label: 'Containers' },
    { id: 'system', icon: Activity, label: 'System Status' },
  ];

  return (
    <div className="w-full max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
      {/* Browser chrome */}
      <div className="bg-slate-800 px-4 py-3 flex items-center gap-3">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <div className="flex-1 bg-slate-700 rounded-md px-3 py-1 text-xs text-gray-400 font-mono text-center">
          localhost:3001 — AI Nextus Dashboard
        </div>
        <div className="w-16" />
      </div>

      {/* Dashboard */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50">
        {/* Header */}
        <header className="bg-white shadow border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-base font-bold bg-gradient-to-r from-purple-800 to-blue-600 bg-clip-text text-transparent leading-none">
              AI Nextus
            </p>
            <p className="text-xs text-gray-400">Container Model Manager</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>{runningCount} Active</span>
            </div>
            <div className="flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5" />
              <span>50.0 GB Used</span>
            </div>
            <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs font-medium">Mock Demo</span>
          </div>
        </header>

        <div className="flex" style={{ minHeight: '420px' }}>
          {/* Sidebar */}
          <aside className="w-48 bg-white shadow-sm border-r border-gray-100 flex-shrink-0">
            <nav className="mt-4">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-left text-xs transition-colors ${
                    activeTab === item.id
                      ? 'bg-gradient-to-r from-purple-50 to-purple-100 border-r-4 border-purple-500 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main */}
          <main className="flex-1 p-5 overflow-auto">
            {activeTab === 'models' && (
              <ModelsTab
                models={models}
                onDownload={handleDownload}
                onStart={handleStart}
                onStop={handleStop}
                onDelete={handleDelete}
              />
            )}
            {activeTab === 'containers' && <ContainersTab models={models} />}
            {activeTab === 'system' && <SystemTab runningCount={runningCount} />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DashboardPreview;
